import React from "react";
import { Button, Card, Icon, Skeleton } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";

/** Honest failure state: the real error text plus a retry button. Never placeholder rows. */
export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <Card className="admin-error-state">
      <span className="admin-error-state__icon"><Icon name="warning" size={24} /></span>
      <div>
        <h3>{t("admin.common.loadErrorTitle")}</h3>
        <p>{message}</p>
      </div>
      <Button icon="settings" onClick={onRetry}>{t("common.retry")}</Button>
    </Card>
  );
}

/** Skeleton block shown while a page is loading. */
export function LoadingState({ rows = 3, className = "" }: { rows?: number; className?: string }) {
  const { t } = useI18n();
  return (
    <Card className={`admin-loading ${className}`} aria-busy="true" aria-label={t("admin.common.loadingLabel")}>
      {Array.from({ length: rows }, (_, index) => (
        <div className="admin-loading__row" key={index}>
          <Skeleton width={38} height={38} />
          <div className="admin-loading__lines">
            <Skeleton width={`${64 - index * 9}%`} height={13} />
            <Skeleton width={`${42 - index * 5}%`} height={10} />
          </div>
        </div>
      ))}
    </Card>
  );
}

export interface TableColumn {
  /** Column label, also reused as the stacked mobile label. */
  label: string;
  /** CSS grid track, e.g. "minmax(210px,1.4fr)". */
  width: string;
}

/**
 * Responsive admin table: real grid columns on wide screens, horizontally
 * scrollable on tablets, stacked label/value card rows below 700px.
 */
export function DataTable({ columns, minWidth = 860, children }: { columns: TableColumn[]; minWidth?: number; children: React.ReactNode }) {
  const { t } = useI18n();
  const template = columns.map((column) => column.width).join(" ");
  const style = { "--admin-cols": template, "--admin-min": `${minWidth}px` } as React.CSSProperties;
  return (
    <div className="admin-table-scroll" role="region" aria-label={t("admin.common.tableAriaLabel")} tabIndex={0}>
      <div className="admin-table" style={style}>
        <div className="admin-table__head">
          {columns.map((column) => <span key={column.label}>{column.label}</span>)}
        </div>
        {children}
      </div>
    </div>
  );
}

/** One table row; each cell gets its column label for the stacked mobile view. */
export function DataRow({ labels, children }: { labels: string[]; children: React.ReactNode }) {
  return (
    <div className="admin-table__row">
      {React.Children.map(children, (child, index) => (
        <span className="admin-table__cell" data-label={labels[index] ?? ""}>{child}</span>
      ))}
    </div>
  );
}

/** Icon button for row actions (44px touch target on mobile). */
export function RowAction({ label, icon, danger, disabled, onClick }: {
  label: string;
  icon: React.ComponentProps<typeof Icon>["name"];
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`admin-row-action ${danger ? "is-danger" : ""}`}
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}

/** Empty list hint (honest zero, not fake rows). */
export function EmptyHint({ children }: { children: React.ReactNode }) {
  return <div className="admin-empty-hint">{children}</div>;
}

type TFunc = ReturnType<typeof useI18n>["t"];

export function healthText(t: TFunc, status: string): string {
  if (status === "healthy") return t("admin.status.healthy");
  if (status === "degraded") return t("admin.status.degraded");
  if (status === "down") return t("admin.status.down");
  if (status === "unknown") return t("admin.status.unknown");
  return status;
}

export function healthTone(status: string): "green" | "amber" | "coral" | "neutral" {
  if (status === "healthy") return "green";
  if (status === "degraded") return "amber";
  if (status === "down") return "coral";
  return "neutral";
}

/** Colored dot + label used for service health rows. */
export function HealthDot({ status }: { status: string }) {
  return <i className={`admin-health-dot is-${healthTone(status)}`} aria-hidden="true" />;
}
